import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import * as _ from 'lodash';
import {getParams} from '../../utils/utility-functions';
import {TixxSelectSubmission, LotteryExecutionSummary} from '../../models';
import {TixxSelectSubmissionStatusTranslationsService} from 'src/app/shared/translations';
import {environment} from '~environments/environment';

/**
 * Service for creating and updating tixx select submissions' entity.
 */
@Injectable({
  providedIn: 'root'
})
export class TixxSelectSubmissionService {
  public API_URL: any = environment.envAPI;

  public submissionList: BehaviorSubject<Array<TixxSelectSubmission>> = new BehaviorSubject<Array<TixxSelectSubmission>>(null);
  public castSubmissionList: any = this.submissionList.asObservable();

  public selectedSubmission: BehaviorSubject<TixxSelectSubmission> = new BehaviorSubject<TixxSelectSubmission>(null);
  public castSelectedSubmission: any = this.selectedSubmission.asObservable();

  constructor(
    private http: HttpClient,
    private statusTranslations: TixxSelectSubmissionStatusTranslationsService
  ) {
  }

  /**
   * Get all submissions for a customer.
   */
  public getAll(customerId: number): Observable<Array<TixxSelectSubmission>> {
    const params = new HttpParams()
      .set('page', '1')
      .set('itemsPerPage', '5');

    return this.http.get<Array<TixxSelectSubmission>>(`${this.API_URL}/l/customers/${customerId}/submissions`, {params}).pipe(
      map(res => res['hydra:member'].map((submissionData: TixxSelectSubmission) => new TixxSelectSubmission(submissionData, this.statusTranslations))),
    );
  }

  /**
   * Create TixxSelectSubmission class.
   */
  public createTixxSelectSubmissionClass(submissions: Array<any>): Array<TixxSelectSubmission> {
    return submissions.map((submission: TixxSelectSubmission) => new TixxSelectSubmission(submission, this.statusTranslations));
  }

  /**
   * Post the iteration filter to create an id that the process wil work with.
   */
  public postIterationFilters(form: any): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/l/iteration-submission-filters`, form);
  }

  /**
   * Get the execution submission summary.
   */
  public getExecutionSubmissionSummary(lotteryId: number, queries: any): Observable<any> {
    let params = new HttpParams();
    if (queries) {
      params = getParams(queries);
    }

    return this.http.get<any>(`${this.API_URL}/l/lotteries/${lotteryId}/execution/submissions/info`, {params}).pipe(
      map(res => new LotteryExecutionSummary(res))
    );
  }

  /**
   * Get all executions api.
   */
  public getExecutionSubmissionEndpoint(lotteryId: number): any {
    const api = `${this.API_URL}/lotteries/${lotteryId}/execution/submissions`;

    const params: Array<any> = [];

    return [api, params];
  }

  /**
   * Get all submissions api.
   */
  public getLotterySubmissionEndpoint(lotteryId: number): any {
    const api = `${this.API_URL}/l/lotteries/${lotteryId}/submissions`;

    const params: Array<any> = [];

    return [api, params];
  }

  /**
   * Get all customer submissions api.
   */
  public getCustomerLotterySubmissionEndpoint(customerId: number): any {
    const api = `${this.API_URL}/l/customers/${customerId}/submissions`;

    const params: Array<any> = [];

    return [api, params];
  }

  /**
   * Submit the execution iteration.
   */
  public postExecutionIterations(data: any): any {
    return this.http.post<any>(`${this.API_URL}/l/executions/iterations`, data);
  }

  /**
   * Get the iteration information.
   */
  public getIterationInfo(iterationId: any): any {
    return this.http.get<any>(`${this.API_URL}/l/executions/iterations/${iterationId}/info`);
  }

  /**
   * Get the iteration information.
   */
  public postIterationReservation(data: any): any {
    return this.http.post<any>(`${this.API_URL}/l/executions/iterations/reservation`, data);
  }

  /**
   * Get all submissions for a lottery.
   */
  public createSubmissionObject(submissions: Array<TixxSelectSubmission>): Array<TixxSelectSubmission> {
    return submissions.map((submissionData: TixxSelectSubmission) => new TixxSelectSubmission(submissionData, this.statusTranslations));
  }

  /**
   * Update the priority status for the submission.
   */
  public editSubmissionPriority(submissionId: string, priority: any): any {
    return this.http.patch<any>(`${this.API_URL}/l/submissions/${submissionId}/priority`, priority).pipe(
      map(res => new TixxSelectSubmission(res, this.statusTranslations))
    );
  }

  /**
   * Deletes submissions.
   */
  public delete(submissionId: string): Observable<TixxSelectSubmission> {
    return this.http.delete<TixxSelectSubmission>(`${this.API_URL}/l/submissions/${submissionId}`);
  }

  /**
   * Set a new submissions list
   */
  public setSubmissionsList(submissionList: Array<TixxSelectSubmission>): void {
    this.submissionList.next(submissionList);
  }

  public setSelectedSubmission(submission: TixxSelectSubmission): void {
    this.selectedSubmission.next(submission);
  }

  /**
   * Delete a certain entry inside the submissions list
   */
  public deleteSubmissionsList(id: string): void {
    const index = this.submissionList.getValue().findIndex(el => el.id === id);
    this.submissionList.getValue().splice(index, 1);
    this.submissionList.next(this.submissionList.getValue());
  }

  /**
   * Update a certain entry in the submission list
   */
  public updateSubmission(submission: TixxSelectSubmission): void {
    const index = this.submissionList.getValue().findIndex(el => el.id === submission.id);
    this.submissionList.getValue()[index] = submission;
  }
}
